// frontend/src/components/User/VehicleTracker.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import useVehicleTracker from '../../hooks/useVehicleTracker';
import DashboardHeader from './DashboardHeader';
import DashboardNavigation from './DashboardNavigation';
import DashboardContent from './DashboardContent';
import NotificationSystem from './NotificationSystem';
import AdvertisementDisplay from './AdvertisementDisplay';
import RouteSubscriptionForm from '../Forms/RouteSubscriptionForm';
import ChangePassword from '../Auth/ChangePassword';
import ContactUs from '../Contact/ContactUs';
import TermsAndConditions from '../Legal/TermsAndConditions';
import SettingsModal from './SettingsModal';

/**
 * Refactored Vehicle Tracker Dashboard
 * Main user dashboard with modular components
 */
const VehicleTracker = ({ onSwitchToConductor, isConductor }) => {
  const { logout, user } = useAuth();

  /* -----------------------------------------------
  +   * Turn the swipe-to-change-tab feature on / off
  +   * --------------------------------------------- */
  const [swipeEnabled, setSwipeEnabled] = useState(true);
  const enableSwipe = () => setSwipeEnabled(true);
  const disableSwipe = () => setSwipeEnabled(false);

  // Use custom hook for state management
  const {
    // Navigation
    activeTab,
    setActiveTab,

    // Add these
    mapFilters,
    userLocation,
    setUserLocation,  // NEW: We need direct access to setUserLocation
    handleFilterChange,
    handleAutoLocate,

    // Data
    publicVehicles,
    userVehicles,
    sharedVehicles,
    busRoutes,
    subscriptions,
    routeSubscriptions,
    ads,
    allPublicAds,

    // Advertisement state
    currentAd,
    setCurrentAd,
    showInterstitial,
    setShowInterstitial,
    currentBannerIndex,
    setCurrentBannerIndex,

    // UI state
    notifications,
    setNotifications,
    loading,
    error,

    // Map state
    selectedVehicleId,
    setSelectedVehicleId,
    mapCenter,
    setMapCenter,
    mapZoom,
    setMapZoom,
    subscribedBusesWithDistance,
    setSubscribedBusesWithDistance,

    // Actions
    fetchAllData,
    handleAdView,
    handleAdClick,
    handleLocateMeClick, // NEW: Dedicated handler for "Locate Me" button

    pushNotificationStatus,
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
    sendTestPushNotification,
  } = useVehicleTracker();

  // Modal state
  const [showRouteSubscriptionForm, setShowRouteSubscriptionForm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Map auto-centering control
  const [shouldAutoCenter, setShouldAutoCenter] = useState(false);

  // NEW: Function to set user location without centering map
  const handleSetUserLocationOnly = (userPosition) => {
    //
    setUserLocation(userPosition);
    // Intentionally NOT setting mapCenter here - that's the key difference from handleAutoLocate
  };

  // Handle settings changes
  const handleSettingsChange = (newSettings) => {
    // Dispatch a custom event to notify DashboardNavigation
    window.dispatchEvent(new CustomEvent('tabSettingsUpdated', { detail: newSettings }));
  };

  // Enhanced vehicle/bus click handlers that switch to map and center location
  const handleVehicleClick = (vehicle) => {
    //

    // Set the selected vehicle
    setSelectedVehicleId(vehicle.id);

    // Switch to map tab
    setActiveTab('map');

    // Center map on vehicle location if available AND signal that this should auto-center
    if (vehicle.last_location) {
      const { latitude, longitude } = vehicle.last_location;
      setMapCenter({ lat: latitude, lng: longitude });
      setMapZoom(16); // Zoom in to show detailed view
      setShouldAutoCenter(true); // Signal that auto-centering is wanted
    }

    // Scroll to top to show the map
    setTimeout(() => {
      // Try to find the map container and scroll to it
      const mapContainer = document.querySelector('.leaflet-container') ||
        document.querySelector('[data-testid="map-container"]') ||
        document.querySelector('.bg-white.rounded-lg.shadow'); // MapView container

      if (mapContainer) {
        mapContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      } else {
        // Fallback: scroll down a reasonable amount
        window.scrollTo({
          top: 400, // Adjust this value based on your layout
          behavior: 'smooth'
        });
      }
    }, 150); // Small delay to ensure tab switch and map render complete
  };

  const handleBusClick = (bus) => {
    //

    // Set the selected bus
    setSelectedVehicleId(bus.id);

    // Switch to map tab
    setActiveTab('map');

    // Center map on bus location if available AND signal that this should auto-center
    if (bus.last_location) {
      const { latitude, longitude } = bus.last_location;
      setMapCenter({ lat: latitude, lng: longitude });
      setMapZoom(16); // Zoom in to show detailed view
      setShouldAutoCenter(true); // Signal that auto-centering is wanted
    }

    // Scroll to top to show the map
    setTimeout(() => {
      // Try to find the map container and scroll to it
      const mapContainer = document.querySelector('.leaflet-container') ||
        document.querySelector('[data-testid="map-container"]') ||
        document.querySelector('.bg-white.rounded-lg.shadow'); // MapView container

      if (mapContainer) {
        mapContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      } else {
        // Fallback: scroll down a reasonable amount
        window.scrollTo({
          top: 400, // Adjust this value based on your layout
          behavior: 'smooth'
        });
      }
    }, 150); // Small delay to ensure tab switch and map render complete
  };

  // State to track enabled tabs for swipe navigation
  const [enabledTabs, setEnabledTabs] = useState({
    map: true,
    'enhanced-subs': true,
    vehicles: false,
    shared: false,
    subscriptions: false,
    schedules: true
  });

  // Load enabled tabs from localStorage on mount
  useEffect(() => {
    const loadEnabledTabs = () => {
      try {
        const savedSettings = localStorage.getItem('userTabSettings');
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          setEnabledTabs(parsed);
        }
      } catch (error) {
      }
    };

    loadEnabledTabs();

    // Listen for settings updates
    const handleTabSettingsUpdate = (e) => {
      setEnabledTabs(e.detail);
    };

    window.addEventListener('tabSettingsUpdated', handleTabSettingsUpdate);

    return () => {
      window.removeEventListener('tabSettingsUpdated', handleTabSettingsUpdate);
    };
  }, []);

  // Tab navigation - only navigate between visible/enabled tabs
  const allTabs = [
    { id: 'map', label: 'Map' },
    { id: 'enhanced-subs', label: 'Alerts' },
    { id: 'schedules', label: 'Schedules' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'vehicles', label: 'My Vehicles' },
    { id: 'shared', label: 'Shared' }
  ];

  const navigateToTab = (direction) => {
    const visibleTabs = allTabs.filter(tab => enabledTabs[tab.id]);
    const visibleTabIds = visibleTabs.map(tab => tab.id);

    // Don't navigate if there are no visible tabs or only one tab
    if (visibleTabIds.length <= 1) {
      return;
    }

    const currentIndex = visibleTabIds.indexOf(activeTab);
    let newIndex;

    if (direction === 'next') {
      newIndex = (currentIndex + 1) % visibleTabIds.length;
    } else {
      newIndex = (currentIndex - 1 + visibleTabIds.length) % visibleTabIds.length;
    }

    setActiveTab(visibleTabIds[newIndex]);
  };

  // Swipe navigation handlers
  const handleSwipeLeft = () => {
    navigateToTab('next');
  };

  const handleSwipeRight = () => {
    navigateToTab('prev');
  };

  // Initialize swipe navigation
  const { swipeHandlers, isSwiping } = useSwipeNavigation(
    handleSwipeLeft,
    handleSwipeRight,
    swipeEnabled
  );

  // Handlers
  const handlePasswordChangeSuccess = (message) => {
    setShowChangePassword(false);
    setNotifications(prev => [...prev, {
      id: Date.now(),
      type: 'success',
      message: message
    }]);
  };

  const handleRefreshData = () => {
    fetchAllData();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefreshData}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" {...swipeHandlers}>
      {/* Header */}
      <DashboardHeader
        onChangePassword={() => setShowChangePassword(true)}
        onContactUs={() => setShowContactUs(true)}
        onShowTerms={() => setShowTermsAndConditions(true)}
        onShowSettings={() => setShowSettings(true)}
        onLogout={logout}
        onSwitchToConductor={onSwitchToConductor}
        isConductor={isConductor}
      />

      {/* Navigation */}
      <DashboardNavigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        userVehicles={userVehicles}
        sharedVehicles={sharedVehicles}
        currentUser={user}
      />

      {/* Swipe indicator */}
      {isSwiping && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[10000] bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
          <div className="flex items-center space-x-2">
            <span>📱</span>
            <span>Swipe to navigate</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <DashboardContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}  // Add tab navigation function
        mapCenter={mapCenter}
        mapZoom={mapZoom}
        setMapCenter={setMapCenter}
        setMapZoom={setMapZoom}
        publicVehicles={publicVehicles}
        userVehicles={userVehicles}
        sharedVehicles={sharedVehicles}
        busRoutes={busRoutes}
        selectedVehicleId={selectedVehicleId}
        setSelectedVehicleId={setSelectedVehicleId}
        onVehicleClick={handleVehicleClick}
        onBusClick={handleBusClick}
        subscribedBusesWithDistance={subscribedBusesWithDistance}
        setSubscribedBusesWithDistance={setSubscribedBusesWithDistance}
        onRefreshData={handleRefreshData}
        subscriptions={subscriptions}
        routeSubscriptions={routeSubscriptions}
        ads={ads}
        enableSwipe={enableSwipe}
        disableSwipe={disableSwipe}

        // Updated: Pass both location functions
        mapFilters={mapFilters}
        userLocation={userLocation}
        onFilterChange={handleFilterChange}
        onAutoLocate={handleAutoLocate}           // Centers map + sets location
        onLocateMeClick={handleLocateMeClick}     // NEW: Dedicated "Locate Me" button handler
        onSetUserLocation={handleSetUserLocationOnly}  // NEW: Only sets location
        shouldAutoCenter={shouldAutoCenter}       // NEW: Control auto-centering
        setShouldAutoCenter={setShouldAutoCenter} // NEW: Reset auto-center flag

        notifications={notifications}
        setNotifications={setNotifications}
        pushNotificationStatus={pushNotificationStatus}
        subscribeToPushNotifications={subscribeToPushNotifications}
        unsubscribeFromPushNotifications={unsubscribeFromPushNotifications}
        sendTestPushNotification={sendTestPushNotification}
        currentUserId={user?.id}
        currentUser={user}  // Add current user
      />

      {/* Notification System */}
      <NotificationSystem
        notifications={notifications}
        setNotifications={setNotifications}
      />

      {/* Modals */}
      {showRouteSubscriptionForm && (
        <RouteSubscriptionForm
          onClose={() => setShowRouteSubscriptionForm(false)}
          onSubmit={() => {
            setShowRouteSubscriptionForm(false);
            handleRefreshData();
          }}
        />
      )}

      {showChangePassword && (
        <ChangePassword
          onClose={() => setShowChangePassword(false)}
          onSuccess={handlePasswordChangeSuccess}
        />
      )}

      {showContactUs && (
        <ContactUs
          onClose={() => setShowContactUs(false)}
        />
      )}

      {showTermsAndConditions && (
        <TermsAndConditions
          onClose={() => setShowTermsAndConditions(false)}
        />
      )}

      {showSettings && (
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          onSettingsChange={handleSettingsChange}
        />
      )}
    </div>
  );
};

export default VehicleTracker;